/*
 * Copyright (c) 2025 BarD Software s.r.o.
 * Author: Dmitry Barashev (dbarashev@ganttproject.cloud)
 *
 */
import {
    CheckoutEventNames, CheckoutEventsData,
    Environments,
    initializePaddle,
    Paddle, PaddleEventData,
} from '@paddle/paddle-js';
import {BillingTopUpPaddleResponse, MeBillingTopUpPaddle} from '@/Routes';

/**
 * This class handles payment with Paddle.
 */
export class PaymentPaddle {
    public errorHandler: (error: string) => void;
    public successHandler: () => void;
    private PADDLE_PROD_TOKEN = 'live_9a7ce4659c8892f0043d25ddaed';
    private PADDLE_PROD_ENVIRONMENT: Environments = 'production';
    private PADDLE_TEST_ENVIRONMENT: Environments = 'sandbox';
    private PADDLE_TEST_TOKEN = 'test_f156005c8844c1678d4774f903a';
    private TEST_ITEMS = {
        gp10: {
            priceId: 'pri_01k4j34w9daqbwgr04ym5f4avp',
        },
    };
    private PROD_ITEMS = {
        gp10: {
            priceId: 'pri_01k4qesk335ntjk774asngdeax',
        },
    };

    private PADDLE_ENVIRONMENT = this.PADDLE_PROD_ENVIRONMENT;
    private PADDLE_TOKEN = this.PADDLE_ENVIRONMENT === 'production' ? this.PADDLE_PROD_TOKEN : this.PADDLE_TEST_TOKEN;
    private ITEMS = this.PADDLE_ENVIRONMENT === 'production' ? this.PROD_ITEMS : this.TEST_ITEMS;
    private ITEMS_LIST = [this.ITEMS.gp10];

    private paddle: Paddle;

    constructor() {
        this.errorHandler = (error: string) => {};
    }

    public init() {
        initializePaddle({
            environment: this.PADDLE_ENVIRONMENT,
            token: this.PADDLE_TOKEN,
            checkout: {
                settings: {
                    displayMode: 'overlay',
                },
            },
            pwCustomer: {},
            eventCallback: (event: PaddleEventData) => {
                this.onPaddleEvent(this.paddle, event);
            },
        }).then((paddle: Paddle | undefined) => {
            if (paddle) {
                console.debug('paddle initialized');
                this.paddle = paddle;
            } else {
                console.debug('paddle not initialized');
            }
        });
    }

    public openCheckout(billingId: string) {
        this.paddle?.Checkout.open({
            items: this.ITEMS_LIST,
            customData: {
                billingId,
            },
        });
    }

    private onPaddleEvent(paddle: Paddle, event: PaddleEventData) {
        const errorHandler = this.errorHandler;
        const successHandler = this.successHandler;
        if (!event.name) {
            return;
        }
        switch (event.name) {
            case CheckoutEventNames.CHECKOUT_COMPLETED: {
                onCheckoutCompleted(event.data, successHandler, errorHandler);
                paddle.Checkout.close();
                break;
            }
        }
    }
}

function onCheckoutCompleted(
    data: CheckoutEventsData, successHandler: () => void, errorHandler: (error: string) => void) {
    MeBillingTopUpPaddle.invoke({
        txnId: data.transaction_id,
        billingId: data.custom_data['billingId'],

    }).then((response: BillingTopUpPaddleResponse) => {
        if (response.status > 0) {
            errorHandler(response.statusMessage);
            console.error('Something went wrong when applying credit' + response.statusMessage);
        } else {
            successHandler();
            console.debug('Payment completed successfully, the account should\'ve been updated.');
        }
    }).fail((error) => {
        errorHandler(`Something went wrong when applying your purchased credit to your account.`);
    });
}
